import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  AkebonoLotSource,
  AkebonoLotTypeEnum,
  CurrentUser,
  LotAiTranslationCreateInput,
  LotSource,
  LotTranslateRequestCreateInput,
  LotTypeEnum,
  TranslationLanguage,
} from '../../../../graphql/service/graphql-auto-main-service';
import { LotTranslationService } from '../../services/lot-translation.service';
import { TranslationTab } from './enums/translation-tab.type';
import { TranslationState } from '../../types/translation.type';
import { LotManualTranslationTabSubmitPayloadType } from './components/lot-manual-translation-tab/types/lot-manual-translation-tab-submit-payload.type';

@Component({
  selector: 'app-lot-translate-request-create-modal',
  templateUrl: './lot-translate-request-create-modal.component.html',
  styleUrls: ['./lot-translate-request-create-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotTranslateRequestCreateModalComponent implements OnInit {
  @Input() lotId: string;
  @Input() lotSource: LotSource;
  @Input() lotType: LotTypeEnum;
  @Input() currentUser: CurrentUser;
  @Input() translationState: TranslationState;

  @Output() translationRequestCreated = new EventEmitter<void>();

  creating$: Observable<boolean>;

  activeTab = TranslationTab.MANUAL;

  aiLanguage = TranslationLanguage.Ru;
  aiLanguages = [TranslationLanguage.Ru];

  readonly tabOrder: TranslationTab[] = [TranslationTab.AI, TranslationTab.MANUAL];

  manualTabPayload: LotManualTranslationTabSubmitPayloadType | null = null;

  constructor(public modal: NzModalRef, private lotTranslationService: LotTranslationService) {}

  get aiFlowEnabled(): boolean {
    return (
      !!this.translationState &&
      (this.translationState.canRequestAiRu || this.translationState.aiReady)
    );
  }

  get manualTabDisabled(): boolean {
    if (!this.translationState) {
      return true;
    }

    return (
      this.translationState.manualReady || (this.aiFlowEnabled && !this.translationState.aiReady)
    );
  }

  get manualTabTooltip(): boolean {
    return this.manualTabDisabled && this.aiFlowEnabled && !this.translationState?.aiReady;
  }

  get aiTabDisabled(): boolean {
    return !this.canRequestAiTranslation;
  }

  get activeTabIndex(): number {
    const index = this.tabOrder.indexOf(this.activeTab);
    return index === -1 ? 0 : index;
  }

  get canRequestAiTranslation(): boolean {
    return !!this.translationState?.canRequestAiRu;
  }

  get isAiTabActive(): boolean {
    return this.activeTab === TranslationTab.AI;
  }

  ngOnInit(): void {
    const shouldStartFromAi = this.aiFlowEnabled && !this.translationState?.aiReady;

    this.activeTab = shouldStartFromAi ? TranslationTab.AI : TranslationTab.MANUAL;
    this.aiLanguage = TranslationLanguage.Ru;
  }

  onTabIndexChange(index: number): void {
    const nextTab = this.tabOrder[index];

    if (nextTab === TranslationTab.MANUAL && this.manualTabDisabled) {
      return;
    }

    if (nextTab === TranslationTab.AI && this.aiTabDisabled) {
      return;
    }

    this.activeTab = nextTab;
  }

  get submitDisabled(): boolean {
    if (this.isAiTabActive) {
      return this.aiTabDisabled;
    }

    return this.manualTabDisabled || !this.manualTabPayload;
  }

  submitActiveTab(): void {
    if (this.isAiTabActive) {
      this.submitAiTranslation();
    } else {
      this.submitManualTranslation();
    }
  }

  private submitAiTranslation(): void {
    if (!this.canRequestAiTranslation) {
      return;
    }

    const input: LotAiTranslationCreateInput = {
      lotId: this.lotId,
      lotType: this.lotType as unknown as AkebonoLotTypeEnum,
      lotSource: this.lotSource as unknown as AkebonoLotSource,
      language: this.aiLanguage,
    };

    this.creating$ = this.lotTranslationService
      .requestAiTranslation(input)
      .pipe(finalize(() => this.onRequestFinished()));
  }

  private submitManualTranslation(): void {
    if (this.manualTabDisabled) {
      return;
    }

    const payload = this.manualTabPayload;

    if (!payload) {
      return;
    }

    const input: LotTranslateRequestCreateInput = {
      lotId: this.lotId,
      lotType: this.lotType,
      lotSource: this.lotSource,
      targetLanguage: payload.language,
      translationMediaType: payload.translationMediaType,
    };

    this.creating$ = this.lotTranslationService
      .requestTranslate(input)
      .pipe(finalize(() => this.onRequestFinished()));
  }

  private onRequestFinished(): void {
    this.translationRequestCreated.emit();
    this.destroyModal();
  }

  destroyModal(): void {
    this.modal.destroy();
  }
}
